import { Button, FAIcon } from '@/components'
import {
  ExportFormat,
  ExportFormatPrompt,
  ExportOption,
} from '@/modules/exports'
import type {
  ExportUserDocumentsMutation,
  ExportUserDocumentsMutationVariables,
  UserDocumentFilterVariables,
} from '@/modules/users'
import { EXPORT_USER_DOCUMENTS_MUTATION } from '@/modules/users/mutations'
import { PERMISSIONS, usePermissions } from '@/utils'
import { useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'

interface Props {
  /** The filters the page is querying with — the export gets the same rows. */
  filters: UserDocumentFilterVariables
}

/**
 * The export action in the user documents page header. Renders nothing for a
 * user without the export permission.
 *
 * A PDF is rendered inline and opened in the print view; anything else is
 * queued and picked up under "My exports" — the same split the vehicle list
 * exports use, so an export behaves the same wherever it is started.
 */
export const UserDocumentsExportButton: React.FC<Props> = ({ filters }) => {
  const translations = useTranslate({
    export: 'common.export',
    error: 'error.generic-server-error',
    queued: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
  })

  const { hasPermissions } = usePermissions()
  const addPrompt = usePrompt()
  const addToast = useToast()
  const navigate = useNavigate()

  const [runExport, { loading }] = useMutation<
    ExportUserDocumentsMutation,
    ExportUserDocumentsMutationVariables
  >(EXPORT_USER_DOCUMENTS_MUTATION, {
    onCompleted(data) {
      const result = data.exportUserDocuments

      if (result?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (result?.html) {
        void navigate('/export', { state: { html: result.html } })
      } else if (result?.ok) {
        addToast('success', translations.queued)
      } else {
        addToast('error', translations.error)
      }
    },
    onError() {
      addToast('error', translations.error)
    },
  })

  async function handleExport() {
    const { data: exportFormat } = await addPrompt<ExportFormat | null>(
      resolve => <ExportFormatPrompt onSubmit={resolve} />
    )
    if (!exportFormat) return

    await runExport({
      variables: {
        exportFormat,
        exportOption:
          exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
        filters,
      },
    })
  }

  if (!hasPermissions(PERMISSIONS.companies.export.exportReports, true)) {
    return null
  }

  return (
    <Button variant="outline" disabled={loading} onClick={handleExport}>
      <FAIcon icon="download" />
      {translations.export}
    </Button>
  )
}
